import React, { useState } from 'react';
import { AlertTriangle, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '../utils';
import { Button } from './Button';

export interface ErrorStateProps {
  title?: string;
  message?: string;
  error?: Error | null;
  onRetry?: () => void;
  className?: string;
}

export const ErrorState: React.FC<ErrorStateProps> = ({
  title = 'Application Recovery Required',
  message = 'An unexpected error occurred while rendering this workspace. Local training data is secure.',
  error,
  onRetry,
  className,
}) => {
  const [showDetails, setShowDetails] = useState(false);

  return (
    <div
      role="alert"
      className={cn(
        'flex flex-col items-center justify-center p-8 text-center rounded-xl bg-surface-1 border border-status-critical/30 max-w-lg mx-auto shadow-lg',
        className
      )}
    >
      <div className="w-12 h-12 rounded-full bg-status-critical/15 text-status-critical flex items-center justify-center mb-4 border border-status-critical/30">
        <AlertTriangle className="w-6 h-6" />
      </div>
      <h3 className="text-lg font-semibold text-foreground-primary mb-2">
        {title}
      </h3>
      <p className="text-sm text-foreground-secondary mb-6 leading-relaxed">
        {message}
      </p>

      <div className="flex items-center gap-3">
        {onRetry && (
          <Button
            variant="primary"
            size="md"
            leftIcon={<RefreshCw className="w-4 h-4" />}
            onClick={onRetry}
          >
            Retry Component
          </Button>
        )}
        <Button
          variant="outline"
          size="md"
          onClick={() => window.location.reload()}
        >
          Reload App
        </Button>
      </div>

      {error && (
        <div className="w-full mt-6 text-left border-t border-border-subtle pt-4">
          <button
            type="button"
            onClick={() => setShowDetails(!showDetails)}
            className="flex items-center justify-between w-full text-xs text-foreground-muted hover:text-foreground-secondary transition-colors"
          >
            <span>Technical Diagnostics</span>
            {showDetails ? (
              <ChevronUp className="w-3.5 h-3.5" />
            ) : (
              <ChevronDown className="w-3.5 h-3.5" />
            )}
          </button>
          {showDetails && (
            <pre className="mt-2 p-3 bg-surface-2 rounded text-xs font-mono text-status-critical overflow-x-auto whitespace-pre-wrap max-h-40">
              {error.message}
              {error.stack && `\n\n${error.stack}`}
            </pre>
          )}
        </div>
      )}
    </div>
  );
};
